import React, { useState, useEffect } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import { users } from '../../services/api';
import { useTranslation } from 'react-i18next';  // Import useTranslation hook

const PasswordRequirement = ({ isMet, label }) => {
  return (
    <div className="flex items-center space-x-2">
      {isMet ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-gray-300" />
      )}
      <span className={`text-sm ${isMet ? 'text-green-500' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
};

const PasswordChangeModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();  // Initialize translation hook
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  // Password requirements state
  const [requirements, setRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasSpecial: false,
    hasUppercase: false,
    hasLowercase: false,
    matches: false
  });

  // Check password requirements
  useEffect(() => {
    const newPassword = passwords.new;
    setRequirements({
      minLength: newPassword.length >= 8,
      hasNumber: /\d/.test(newPassword),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      matches: newPassword === passwords.confirm && newPassword !== ''
    });
  }, [passwords.new, passwords.confirm]);

  // Check if all requirements are met
  const areAllRequirementsMet = () => {
    return Object.values(requirements).every(req => req);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!areAllRequirementsMet()) {
      setError(t('auth.passwordChange.meetRequirements', 'Please meet all password requirements'));
      return;
    }

    if (passwords.new === passwords.current) {
      setError(t('auth.passwordChange.mustBeDifferent', 'New password must be different from current password'));
      return;
    }

    try {
      setIsSubmitting(true);
      await users.changePassword(passwords);  // ← Změna z 'auth' na 'users'
      
      setSuccess(t('auth.passwordChange.success', 'Password changed successfully'));
      setTimeout(() => {
        onClose();
        setSuccess('');
        setPasswords({ current: '', new: '', confirm: '' });
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || t('auth.passwordChange.error', 'Failed to change password'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[28rem] shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">{t('auth.passwordChange.title', 'Change Password')}</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-500"
            disabled={isSubmitting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-2 text-sm text-red-600 bg-red-50 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="p-2 text-sm text-green-600 bg-green-50 rounded">
              {success}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('auth.passwordChange.currentPassword', 'Current Password')}
            </label>
            <input
              type="password"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={passwords.current}
              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('auth.passwordChange.newPassword', 'New Password')}
            </label>
            <input
              type="password"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={passwords.new}
              onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('auth.passwordChange.confirmPassword', 'Confirm New Password')}
            </label>
            <input
              type="password"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={passwords.confirm}
              onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
              disabled={isSubmitting}
            />
          </div>

          {/* Password Requirements Section */}
          <div className="p-4 bg-gray-50 rounded-md">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              {t('auth.passwordChange.requirements.title', 'Password Requirements')}
            </h4>
            <div className="space-y-2">
              <PasswordRequirement 
                isMet={requirements.minLength} 
                label={t('auth.passwordChange.requirements.minLength', 'At least 8 characters long')} 
              />
              <PasswordRequirement 
                isMet={requirements.hasUppercase} 
                label={t('auth.passwordChange.requirements.hasUppercase', 'Contains at least one uppercase letter')} 
              />
              <PasswordRequirement 
                isMet={requirements.hasLowercase} 
                label={t('auth.passwordChange.requirements.hasLowercase', 'Contains at least one lowercase letter')} 
              />
              <PasswordRequirement 
                isMet={requirements.hasNumber} 
                label={t('auth.passwordChange.requirements.hasNumber', 'Contains at least one number')} 
              />
              <PasswordRequirement 
                isMet={requirements.hasSpecial} 
                label={t('auth.passwordChange.requirements.hasSpecial', 'Contains at least one special character')} 
              />
              <PasswordRequirement 
                isMet={requirements.matches} 
                label={t('auth.passwordChange.requirements.matches', 'Passwords match')} 
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              disabled={isSubmitting}
            >
              {t('common.actions.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting || !areAllRequirementsMet()}
            >
              {isSubmitting 
                ? t('auth.passwordChange.changing', 'Changing Password...') 
                : t('auth.passwordChange.submit', 'Change Password')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordChangeModal;